// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson
// Additional terms under AGPL section 7(b) apply — see ADDITIONAL-TERMS.md.

import type { CaseResult, Config, Pair, ParsedSide, ScorerResult, Summary, Verdict } from './types';
import { classify, trajectoryScore } from './score/classify';
import {
  changeEditDistance,
  makeSchemaChecker,
  SCORERS,
  scoreCarried,
  scoreCost,
  scoreErrored,
  scoreExpected,
  scoreIsJson,
  scoreLatency,
  scoreLengthBounds,
  scoreSchema,
  type SchemaChecker,
} from './score/scorers';
import { joinRows } from './join/join';
import { summariseTrajectory } from './traj/summarise';

export interface RunResult {
  readonly summary: Summary;
  readonly cases: readonly CaseResult[];
  readonly droppedKeys: readonly string[];
  readonly schemaError: string | null;
}

function caseId(pair: Pair, index: number): string {
  const r = pair.a ?? pair.b;
  if (!r) return `case-${index}`;
  if (r.key) return r.key.replace(/\u001f/g, ' · ');
  const t = r.inputText.trim().replace(/\s+/g, ' ');
  return t.length > 0 ? (t.length > 64 ? `${t.slice(0, 63)}…` : t) : `row ${r.index + 1}`;
}

export function runComparison(a: ParsedSide, b: ParsedSide, cfg: Config): RunResult {
  const notes: string[] = [...a.notes, ...b.notes];

  const filter = cfg.fanoutFilter;
  const aRows = filter ? a.rows.filter((r) => filter.includes(r.fanoutId)) : a.rows;
  const bRows = filter ? b.rows.filter((r) => filter.includes(r.fanoutId)) : b.rows;

  // Each fanout branch is joined and compared independently: comparing gpt-5.4's answer to
  // gpt-5.4-mini's answer would report a regression on essentially every row.
  const branchIds = new Set<string>([...aRows.map((r) => r.fanoutId), ...bRows.map((r) => r.fanoutId)]);
  const onlyA = [...branchIds].filter(
    (id) => aRows.some((r) => r.fanoutId === id) && !bRows.some((r) => r.fanoutId === id),
  );
  const onlyB = [...branchIds].filter(
    (id) => bRows.some((r) => r.fanoutId === id) && !aRows.some((r) => r.fanoutId === id),
  );
  if (onlyA.length > 0 || onlyB.length > 0) {
    notes.push(
      `${onlyA.length + onlyB.length} branch${onlyA.length + onlyB.length === 1 ? '' : 'es'} exist in only one ` +
        'of the two runs and were not compared.',
    );
  }

  const schemaResult = makeSchemaChecker(cfg.schema);
  const schemaError = schemaResult && 'error' in schemaResult ? schemaResult.error : null;
  const checker: SchemaChecker | null = schemaResult && !('error' in schemaResult) ? schemaResult : null;
  if (schemaError) notes.push(`The schema was not used: ${schemaError}`);

  const droppedKeys = new Set<string>();
  const cases: CaseResult[] = [];
  const join = { keyFunction: '', exact: 0, similarity: 0, ambiguous: 0, method: '' };
  let dropped = 0;
  let added = 0;
  let unmatched = 0;

  for (const branch of branchIds) {
    if (!aRows.some((r) => r.fanoutId === branch) || !bRows.some((r) => r.fanoutId === branch)) continue;
    const ba = aRows.filter((r) => r.fanoutId === branch);
    const bb = bRows.filter((r) => r.fanoutId === branch);
    const jr = joinRows(ba, bb);
    if (!join.keyFunction) join.keyFunction = jr.keyFunction;
    join.exact += jr.exact;
    join.similarity += jr.similarity;
    join.ambiguous += jr.ambiguous;
    notes.push(...jr.notes);

    for (const pair of jr.pairs) {
      const idx = cases.length;
      if (!pair.a || !pair.b) {
        const verdict: Verdict = pair.a ? 'dropped' : 'added';
        if (pair.a) dropped++;
        else added++;
        if (pair.method === 'unmatched') unmatched++;
        cases.push({
          id: caseId(pair, idx),
          fanoutId: branch,
          fanoutLabel: (pair.a ?? pair.b)!.fanoutLabel,
          verdict,
          headlineScorer: null,
          headlineReason:
            verdict === 'dropped'
              ? 'this row is in the before run and not in the after run'
              : 'this row is new in the after run',
          scores: [],
          changes: [],
          trajectory: null,
          pair,
          editDistance: null,
        });
        continue;
      }

      const scores: ScorerResult[] = [];
      const enabled = (id: string): boolean => cfg.enabled[id] !== false;

      if (enabled('errored')) scores.push(scoreErrored(pair.a, pair.b));
      if (enabled('carried-assertion')) scores.push(scoreCarried(pair.a, pair.b));
      if (enabled('is-json')) scores.push(scoreIsJson(pair.a, pair.b));
      if (enabled('json-schema')) scores.push(scoreSchema(pair.a, pair.b, checker));
      if (enabled('expected-match')) scores.push(scoreExpected(pair.a, pair.b, 'equals'));
      if (enabled('length-bounds')) {
        scores.push(scoreLengthBounds(pair.a, pair.b, cfg.thresholds.lengthMin, cfg.thresholds.lengthMax));
      }
      if (enabled('latency-regression')) scores.push(scoreLatency(pair.a, pair.b, cfg));
      if (enabled('cost-regression')) scores.push(scoreCost(pair.a, pair.b, cfg));

      const hasTraj = pair.a.toolCalls.length > 0 || pair.b.toolCalls.length > 0;
      const trajectory = hasTraj
        ? summariseTrajectory(pair.a.toolCalls, pair.b.toolCalls, cfg.rules, { droppedKeys })
        : null;
      const ts = enabled('trajectory') ? trajectoryScore(trajectory) : null;
      if (ts) scores.push(ts);

      const cls = classify(scores, trajectory);
      const editDistance = changeEditDistance(pair.a, pair.b);

      // A pair inside an ambiguous group cannot be trusted to be the same test case, so it never
      // contributes to the per-row regression count. The group's net change is reported instead.
      const verdict: Verdict = pair.confidence === 'ambiguous' ? 'ambiguous-group' : cls.verdict;

      cases.push({
        id: caseId(pair, idx),
        fanoutId: branch,
        fanoutLabel: pair.b.fanoutLabel,
        verdict,
        headlineScorer: verdict === 'ambiguous-group' ? null : cls.headlineScorer,
        headlineReason:
          verdict === 'ambiguous-group'
            ? 'this row shares its input with another row in the same file, so it cannot be paired reliably'
            : cls.headlineReason,
        scores: cls.resolved,
        changes: [
          {
            scorer: 'edit-distance',
            a: 'na',
            b: 'na',
            flipped: false,
            fixed: false,
            detail: `${editDistance} characters differ`,
          },
        ],
        trajectory,
        pair,
        editDistance,
      });
    }
  }

  join.method = join.exact > 0 && join.similarity === 0 ? 'exact' : 'mixed';

  /* ── Ambiguous groups: a multiset delta, never a bijection ─────────────────
     Pairing indistinguishable rows by position makes the REGRESSION COUNT depend on an arbitrary
     bijection — measured, the same three-row group reports 1 or 2 regressions depending on which
     permutation you happen to pick. So the members are excluded from the per-row count entirely
     and the group contributes a net change in failures, which is permutation-invariant. */
  const ambiguousCases = cases.filter((c) => c.verdict === 'ambiguous-group');
  let ambiguousNetDelta = 0;
  {
    const failing = (c: CaseResult, side: 'a' | 'b'): number =>
      c.scores.some((s) => (side === 'a' ? s.a : s.b) === 'fail') ? 1 : 0;
    for (const c of ambiguousCases) ambiguousNetDelta += failing(c, 'b') - failing(c, 'a');
  }

  const flipsByScorer: Record<string, number> = {};
  const fixesByScorer: Record<string, number> = {};
  for (const s of SCORERS) {
    flipsByScorer[s.id] = 0;
    fixesByScorer[s.id] = 0;
  }
  let regressed = 0;
  let errored = 0;
  let diverged = 0;
  let fixed = 0;
  let unchanged = 0;

  for (const c of cases) {
    switch (c.verdict) {
      case 'regression':
        regressed++;
        if (c.headlineScorer) flipsByScorer[c.headlineScorer] = (flipsByScorer[c.headlineScorer] ?? 0) + 1;
        break;
      case 'errored':
        errored++;
        flipsByScorer['errored'] = (flipsByScorer['errored'] ?? 0) + 1;
        break;
      case 'diverged':
      case 'diverged-args':
        diverged++;
        break;
      case 'fixed':
        fixed++;
        if (c.headlineScorer) fixesByScorer[c.headlineScorer] = (fixesByScorer[c.headlineScorer] ?? 0) + 1;
        break;
      case 'unchanged':
        unchanged++;
        break;
      default:
        break;
    }
  }

  /* ── The capability check ─────────────────────────────────────────────────
     "0 rows got worse" is only an honest sentence when something was actually able to look. A
     plain completions dump with no assertions, no reference column and no metrics gives every
     scorer nothing to do — and printing a confident zero over that is the worst output this tool
     can produce. So we record WHY each scorer could not fire, and the UI changes its headline
     entirely when none of them could. */
  const inapplicable: { scorer: string; why: string }[] = [];
  const comparable = cases.filter((c) => c.pair.a && c.pair.b);
  for (const def of SCORERS) {
    if (cfg.enabled[def.id] === false) continue;
    let sawUsable = false;
    let why = def.requires ?? 'nothing to compare';
    for (const c of comparable) {
      const s = c.scores.find((x) => x.scorer === def.id);
      if (!s) continue;
      if (s.a !== 'na' || s.b !== 'na') {
        sawUsable = true;
        break;
      }
      why = s.detail || why;
    }
    if (!sawUsable) inapplicable.push({ scorer: def.id, why });
  }

  const summary: Summary = {
    cases: cases.length,
    regressed,
    errored,
    diverged,
    fixed,
    unchanged,
    dropped,
    added,
    unmatched,
    ambiguousGroups: new Set(ambiguousCases.map((c) => c.pair.ambiguousGroupSize)).size,
    ambiguousRows: ambiguousCases.length,
    ambiguousNetDelta,
    flipsByScorer,
    fixesByScorer,
    inapplicable,
    join,
    notes: [...new Set(notes)],
  };

  return { summary, cases, droppedKeys: [...droppedKeys], schemaError };
}

const SCORER_SENTENCE: Readonly<Record<string, string>> = {
  'carried-assertion': 'failed an assertion your eval already had',
  'is-json': 'stopped returning valid JSON',
  'json-schema': 'stopped matching your schema',
  'expected-match': 'stopped matching the expected value',
  'length-bounds': 'fell outside your length bounds',
  'latency-regression': 'got slower',
  'cost-regression': 'got more expensive',
  trajectory: 'lost or replaced a tool step',
  errored: 'errored',
};

export interface Headline {
  readonly primary: string;
  readonly secondary: string | null;
  /** True when no verdict scorer could look at this pair of files at all. */
  readonly blind: boolean;
}

/**
 * The headline is GENERATED from the summary, never transcribed.
 *
 * A hand-written headline string in a test is a headline that can disagree with the table beneath
 * it, and on a tool whose only asset is that its numbers can be trusted, the demo failing its own
 * arithmetic on the first click is fatal.
 */
export function renderHeadline(summary: Summary): Headline {
  /* `errored` can always look — it only needs a status code or an empty string — so counting it as
     coverage would make the tool claim it checked something whenever it checked nothing. What
     matters is whether any scorer could examine the CONTENT. If none could, "nothing got worse" is
     a sentence about a file we never read, and it is the worst output this tool can produce. */
  const contentScorers = SCORERS.filter(
    (s) => !['errored', 'latency-regression', 'cost-regression'].includes(s.id),
  );
  const usableContent = contentScorers.filter((s) => !summary.inapplicable.some((i) => i.scorer === s.id));

  if (usableContent.length === 0 && summary.cases > 0 && summary.regressed === 0) {
    const errs =
      summary.errored > 0
        ? ` ${summary.errored} row${summary.errored === 1 ? '' : 's'} errored, which is the one thing it could see.`
        : '';
    return {
      primary: 'Nothing here can be objectively checked.',
      secondary:
        'These files carry no assertion results, no reference column and no structured output, so ' +
        'backslide cannot tell you whether the second run is worse. The rows below are sorted by ' +
        `how much they changed — that is a reading list, not a verdict.${errs}`,
      blind: true,
    };
  }

  if (summary.regressed === 0) {
    const bits: string[] = [];
    if (summary.errored > 0) bits.push(`${summary.errored} errored`);
    if (summary.diverged > 0) bits.push(`${summary.diverged} took a different path`);
    if (summary.fixed > 0) bits.push(`${summary.fixed} got better`);
    return {
      primary: 'Nothing got worse.',
      secondary: bits.length > 0 ? `${bits.join(', ')}.` : null,
      blind: false,
    };
  }

  let topScorer: string | null = null;
  let topCount = 0;
  for (const [id, n] of Object.entries(summary.flipsByScorer)) {
    if (id === 'errored') continue; // infrastructure noise never leads
    if (n > topCount) {
      topCount = n;
      topScorer = id;
    }
  }

  const primary = `${summary.regressed} row${summary.regressed === 1 ? '' : 's'} got worse.`;
  const verb = topScorer ? (SCORER_SENTENCE[topScorer] ?? 'changed') : '';
  const secondary =
    topScorer && topCount > 0
      ? topCount === summary.regressed
        ? topCount === 1
          ? `It ${verb}.`
          : `They all ${verb}.`
        : `${topCount} of them ${verb}.`
      : null;
  return { primary, secondary, blind: false };
}
